import {
  WB_MEDICAL_BENEFIT_FIELDS,
  type WbBenefitGroup,
} from "./wbBenefitFields";

/**
 * Per-plan-type benefit field templates (Phase 12).
 *
 * Every plan type gets a benefits editor shaped like the medical one — grouped
 * fields with free-text values ("$50 Copay", "80%", "Included") stored as
 * rawBenefits { name, value, network } entries — using type-specific fields
 * instead of the medical set. Field lists come from Brady's Aug-2026 matrix.
 *
 * `name` is the storage key on rawBenefits; renaming a field orphans existing
 * values into the "Other benefits" display section, so treat names as frozen
 * once shipped.
 *
 * Network columns:
 *  - "medical": in-network + out-of-network, out hidden for HMO plans
 *  - "both":    always in-network + out-of-network (dental)
 *  - "none":    single value per field (everything else)
 *
 * Life/AD&D, STD, LTD keep their structured Coverage Config (amounts,
 * elimination periods…) — these template fields are the SBC-style summary
 * layer shown alongside it. Same for accident/critical-illness supplemental
 * config. FSA/transit/pet and other unlisted types keep the generic
 * benefit-block editor.
 */

export type BenefitTemplateNetworkColumns = "medical" | "both" | "none";

export interface BenefitFieldTemplate {
  groups: WbBenefitGroup[];
  networkColumns: BenefitTemplateNetworkColumns;
}

const single = (names: string[]): WbBenefitGroup["fields"] =>
  names.map((name) => ({ name, label: name }));

export const BENEFIT_FIELD_TEMPLATES: Record<string, BenefitFieldTemplate> = {
  health: { groups: WB_MEDICAL_BENEFIT_FIELDS, networkColumns: "medical" },

  dental: {
    networkColumns: "both",
    groups: [
      {
        group: "Cost shares",
        fields: [
          { name: "Deductible (Individual/Family)", label: "Deductible", split: true },
          { name: "Annual Maximum", label: "Annual max" },
        ],
      },
      {
        group: "Coverage",
        fields: [
          { name: "Preventive Services", label: "Preventive" },
          { name: "Basic Services", label: "Basic" },
          { name: "Major Services", label: "Major" },
        ],
      },
      {
        group: "Orthodontia",
        fields: [
          { name: "Orthodontia", label: "Orthodontia" },
          { name: "Orthodontia Lifetime Maximum", label: "Ortho lifetime max" },
        ],
      },
      { group: "Network", fields: single(["Dental Network"]) },
    ],
  },

  vision: {
    networkColumns: "none",
    groups: [
      {
        group: "Copays & allowances",
        fields: [
          { name: "Exam Copay", label: "Exam copay" },
          { name: "Materials Copay", label: "Materials copay" },
          { name: "Frame Allowance", label: "Frame allowance" },
          { name: "Contact Lens Allowance", label: "Contact lens allowance" },
        ],
      },
      {
        group: "Frequencies",
        fields: [
          { name: "Exam Frequency", label: "Exam frequency" },
          { name: "Lens Frequency", label: "Lens frequency" },
          { name: "Frame Frequency", label: "Frame frequency" },
        ],
      },
      { group: "Network", fields: single(["Vision Network"]) },
    ],
  },

  life: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefit",
        fields: [
          { name: "Benefit Amount / Salary Multiple", label: "Benefit amount / salary multiple" },
          { name: "Maximum Benefit", label: "Maximum benefit" },
          { name: "Guarantee Issue", label: "Guarantee issue" },
        ],
      },
      {
        group: "Provisions",
        fields: [
          { name: "Age Reduction Schedule", label: "Age reduction schedule" },
          { name: "AD&D Included", label: "AD&D included" },
          { name: "Portability / Conversion", label: "Portability / conversion" },
        ],
      },
    ],
  },

  std: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefit",
        fields: [
          { name: "Benefit Percentage", label: "Benefit %" },
          { name: "Weekly Maximum", label: "Weekly max" },
          { name: "Benefit Duration", label: "Benefit duration" },
        ],
      },
      {
        group: "Provisions",
        fields: [
          { name: "Elimination Period - Accident", label: "Elimination period — accident" },
          { name: "Elimination Period - Sickness", label: "Elimination period — sickness" },
          { name: "Pre-Existing Condition Limitation", label: "Pre-existing limitation" },
        ],
      },
    ],
  },

  ltd: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefit",
        fields: [
          { name: "Benefit Percentage", label: "Benefit %" },
          { name: "Monthly Maximum", label: "Monthly max" },
          { name: "Benefit Duration", label: "Benefit duration" },
        ],
      },
      {
        group: "Provisions",
        fields: [
          { name: "Elimination Period", label: "Elimination period" },
          { name: "Own Occupation Period", label: "Own occupation period" },
          { name: "Pre-Existing Condition Limitation", label: "Pre-existing limitation" },
        ],
      },
    ],
  },

  accident: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefits",
        fields: [
          { name: "Accident Benefit Level", label: "Accident benefit level" },
          { name: "Emergency Room Benefit", label: "ER benefit" },
          { name: "Hospital Admission Benefit", label: "Hospital admission" },
          { name: "Hospital Confinement Benefit", label: "Hospital confinement" },
          { name: "Fracture / Dislocation Benefit", label: "Fracture / dislocation" },
          { name: "Accidental Death Benefit", label: "Accidental death" },
          { name: "Wellness Benefit", label: "Wellness benefit" },
        ],
      },
    ],
  },

  critical_illness: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefit amounts",
        fields: [
          { name: "Employee Benefit Amount", label: "Employee benefit amount" },
          { name: "Spouse Benefit Amount", label: "Spouse benefit" },
          { name: "Child Benefit Amount", label: "Child benefit" },
          { name: "Guarantee Issue", label: "Guarantee issue" },
        ],
      },
      {
        group: "Provisions",
        fields: [
          { name: "Covered Conditions Summary", label: "Covered conditions" },
          { name: "Recurrence Benefit", label: "Recurrence benefit" },
          { name: "Wellness Benefit", label: "Wellness benefit" },
        ],
      },
    ],
  },

  cancer: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefits",
        fields: [
          { name: "Initial Diagnosis Benefit", label: "Initial diagnosis benefit" },
          { name: "Hospital Benefit", label: "Hospital benefit" },
          { name: "Treatment Benefit", label: "Treatment benefit" },
          { name: "Surgery Benefit", label: "Surgery benefit" },
          { name: "Screening / Wellness Benefit", label: "Screening / wellness" },
          { name: "Maximum Benefit", label: "Maximum benefit" },
        ],
      },
    ],
  },

  hospital_indemnity: {
    networkColumns: "none",
    groups: [
      {
        group: "Benefits",
        fields: [
          { name: "Admission Benefit", label: "Admission benefit" },
          { name: "Daily Hospital Benefit", label: "Daily hospital benefit" },
          { name: "ICU Benefit", label: "ICU benefit" },
          { name: "Maximum Confinement Days", label: "Max confinement days" },
          { name: "Observation Benefit", label: "Observation benefit" },
          { name: "Pregnancy Covered", label: "Pregnancy covered" },
        ],
      },
    ],
  },

  telehealth: {
    networkColumns: "none",
    groups: [
      {
        group: "Coverage",
        fields: [
          { name: "Vendor / Network", label: "Vendor / network" },
          { name: "General Medical Copay", label: "General medical copay" },
          { name: "Behavioral Health Copay", label: "Behavioral health copay" },
          { name: "Dermatology / Specialty Services", label: "Dermatology / specialty" },
          { name: "24/7 Access", label: "24/7 access" },
          { name: "Family Coverage", label: "Family coverage" },
        ],
      },
    ],
  },

  legal: {
    networkColumns: "none",
    groups: [
      {
        group: "Plan",
        fields: [
          { name: "Network / Vendor", label: "Network / vendor" },
          { name: "Employee Cost", label: "Employee cost" },
          { name: "Family Coverage", label: "Family coverage" },
        ],
      },
      {
        group: "Covered services",
        fields: [
          { name: "Consultation Services", label: "Consultation" },
          { name: "Estate Planning", label: "Estate planning" },
          { name: "Family Law", label: "Family law" },
          { name: "Real Estate Matters", label: "Real estate" },
          { name: "Other Covered Services", label: "Other covered services" },
        ],
      },
    ],
  },

  id_theft: {
    networkColumns: "none",
    groups: [
      {
        group: "Plan",
        fields: [
          { name: "Vendor", label: "Vendor" },
          { name: "Employee Cost", label: "Employee cost" },
          { name: "Family Coverage", label: "Family coverage" },
        ],
      },
      {
        group: "Protection",
        fields: [
          { name: "Credit Monitoring", label: "Credit monitoring" },
          { name: "Identity Restoration Services", label: "Identity restoration" },
          { name: "Identity Theft Insurance Amount", label: "Insurance amount" },
          { name: "Dark Web Monitoring", label: "Dark web monitoring" },
        ],
      },
    ],
  },
};

// AD&D and voluntary life mirror life's summary fields.
BENEFIT_FIELD_TEMPLATES.add = BENEFIT_FIELD_TEMPLATES.life;
BENEFIT_FIELD_TEMPLATES.voluntary_life = BENEFIT_FIELD_TEMPLATES.life;

/**
 * Template for a plan type, or null for types that keep the generic
 * benefit-block editor (FSA, pet, transit, chiro, acupuncture, EAP, …).
 * "medical" normalizes to the health template.
 */
export function getBenefitFieldTemplate(
  planType: string | undefined | null,
): BenefitFieldTemplate | null {
  if (!planType) return null;
  const normalized = planType.toLowerCase().trim();
  if (normalized === "medical") return BENEFIT_FIELD_TEMPLATES.health;
  return BENEFIT_FIELD_TEMPLATES[normalized] ?? null;
}
